// placesVmBuilder.js — monta o PlacesVM (@version 2) a partir da massa crua da API.
//
// Função PURA: recebe dados, devolve o vm. Nenhum fetch, nenhum hook, nenhum estado. É aqui que
// mora a regra de negócio da tela — a View só lê o resultado.
//
// ────────────────────────────────────────────────────────────────────────────
// "ESTE LOCAL RECEBE AGENDAMENTO?" — de onde vem a resposta
// ────────────────────────────────────────────────────────────────────────────
// O veredito de cada AGENDA não é refeito aqui: vem de `buildScheduleCard` (Agendas), a mesma
// função que pinta a tela de Agendas. Uma regra só, nunca uma cópia dela.
//
// Por cima disso, o local só cruza a estrutura: um serviço do local aparece para o cliente quando
// alguém o faz AQUI (vínculo serviço × profissional no local) e uma agenda desse profissional
// NESTE local está recebendo e tem um turno ligado que oferece o serviço. É o mesmo caminho do
// bookingFlow: local → serviço → quem faz → agenda → turno.
//
// Pausar é decisão do dono, nunca pendência (isByChoice). "Sem endereço" é informação: o local
// pode ser uma "Cadeira VIP" dentro de outra unidade.
//
// ────────────────────────────────────────────────────────────────────────────
// TEXTOS SEM GÊNERO
// ────────────────────────────────────────────────────────────────────────────
// O nome do local vem do nicho: Local, Estabelecimento, Consultório — mas também Clínica, Unidade,
// Sala (medido nos 13 temas em 29/10/2026). Por isso nenhum texto põe artigo ou "novo/nova" na
// frente do nome: "Cadastrar unidade", "3 salas", "Só daqui".

import { formatCurrency, formatDuration, formatTime } from '../../utils/formatters';
import { getInitials } from '../../utils/professionalNameUtils';
import { maskPhoneBR } from '../../utils/masks';
import { buildScheduleCard, daysLabel } from '../schedules/buildSchedulesVm';

const BRT = 'America/Sao_Paulo';
const DIA_MS = 86_400_000;
const PAGES_URL = import.meta.env.VITE_PAGES_URL || 'https://pages.agendario.com.br';
const DIAS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nome A → Z' },
  { value: 'name_desc', label: 'Nome Z → A' },
  { value: 'city_asc', label: 'Cidade' },
  { value: 'busiest', label: 'Mais agendados' },
];

// Catálogo PageType (int) — o mesmo vocabulário de Páginas.
const TIPO_DE_PAGINA = { 1: 'Link na bio', 2: 'Página do profissional', 3: 'Página da empresa' };
const ESCOPO_DA_PAGINA = { account: 'De toda a conta', place: 'Só daqui', professional: 'De quem atende aqui' };

// PaymentType chega como string camelCase (JsonStringEnumConverter). Transferência vira 'other'.
const TIPO_DE_PAGAMENTO = { pix: 'pix', cash: 'cash', debitCard: 'debitCard', creditCard: 'creditCard', healthInsurance: 'healthInsurance', boleto: 'boleto' };

const plural = (n, um, muitos) => `${n} ${n === 1 ? um : muitos}`;
const texto = (v) => (typeof v === 'string' && v.trim()) || null;
const minusculo = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const porNome = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR');
const lista = (nomes) => (nomes.length <= 1 ? nomes.join('') : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`);

// ── datas (dia de Brasília) ───────────────────────────────────────────────────
const diaBRT = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: BRT, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const valida = (iso) => { const d = iso ? new Date(iso) : null; return d && !Number.isNaN(d.getTime()) ? d : null; };

/** 'hoje às 14:30' | 'amanhã às 09:00' | 'sáb, 01/11 às 09:00' (nesta semana) | '15/11 às 10:00' */
function quando(iso, now) {
  const d = valida(iso);
  if (!d) return null;
  const dia = diaBRT(d);
  const hora = formatTime(iso);
  if (dia === diaBRT(now)) return `hoje às ${hora}`;
  if (dia === diaBRT(new Date(now.getTime() + DIA_MS))) return `amanhã às ${hora}`;
  const [, m, dd] = dia.split('-');
  const distancia = (Date.parse(dia) - Date.parse(diaBRT(now))) / DIA_MS;
  if (distancia > 0 && distancia < 7) {
    const semana = new Intl.DateTimeFormat('pt-BR', { timeZone: BRT, weekday: 'short' }).format(d).replace('.', '');
    return `${semana}, ${dd}/${m} às ${hora}`;
  }
  return `${dd}/${m} às ${hora}`;
}

/** '18/10' no ano corrente, '18/10/2025' fora dele. */
function diaCurto(iso, now) {
  const d = valida(iso);
  if (!d) return null;
  const [a, m, dd] = diaBRT(d).split('-');
  return a === diaBRT(now).slice(0, 4) ? `${dd}/${m}` : `${dd}/${m}/${a}`;
}

/** 'jan/2026' */
function mesAno(iso) {
  const d = valida(iso);
  if (!d) return null;
  const mes = new Intl.DateTimeFormat('pt-BR', { timeZone: BRT, month: 'short' }).format(d).replace('.', '');
  return `${mes}/${new Intl.DateTimeFormat('pt-BR', { timeZone: BRT, year: 'numeric' }).format(d)}`;
}

const diasEntre = (inicioIso, fimIso) =>
  Math.round((Date.parse(diaBRT(new Date(fimIso))) - Date.parse(diaBRT(new Date(inicioIso)))) / DIA_MS) + 1;

// ── rótulos do nicho ──────────────────────────────────────────────────────────

/** Tema » labels → PlacesLabels. `examples` vem do tema como "Ex: A, B, C". */
export function buildPlacesLabels(getLabel) {
  const title = getLabel?.('places') || 'Locais';
  const singular = getLabel?.('placesSingular') || 'Local';
  const exemplos = String(getLabel?.('placesExamples') || '')
    .replace(/^\s*ex(emplos?)?\s*:\s*/i, '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    labels: {
      title,
      singular,
      subtitle: getLabel?.('placesDescription') || 'Onde você atende',
      actionLabel: `Cadastrar ${minusculo(singular)}`,
      searchPlaceholder: 'Buscar por nome, bairro, cidade ou telefone',
    },
    examples: exemplos,
  };
}

// ── peças do cartão ───────────────────────────────────────────────────────────

function montarEndereco(p) {
  const rua = texto(p.addressStreet);
  const linha1 = rua
    ? `${rua}${texto(p.addressNumber) ? `, ${p.addressNumber.trim()}` : ''}${texto(p.addressComplement) ? ` — ${p.addressComplement.trim()}` : ''}`
    : texto(p.address);
  const cidade = texto(p.addressCity);
  const uf = texto(p.addressState);
  const cidadeUf = cidade && uf ? `${cidade}/${uf}` : cidade || uf;
  const bairro = texto(p.addressNeighborhood);
  const linha2 = [bairro, cidadeUf, texto(p.addressZipCode)].filter(Boolean).join(' · ') || null;
  if (!linha1 && !cidadeUf) return { isMissing: true, line1: null, line2: null, shortLabel: null, mapsUrl: null };
  const busca = [rua ? `${rua}${texto(p.addressNumber) ? `, ${p.addressNumber.trim()}` : ''}` : linha1, cidade, uf].filter(Boolean).join(', ');
  return {
    isMissing: false,
    line1: linha1,
    line2: linha2,
    shortLabel: [bairro, cidadeUf].filter(Boolean).join(', ') || null,
    mapsUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(busca)}`,
  };
}

function montarContato(p) {
  let digitos = String(p.phone || '').replace(/\D/g, '');
  if (digitos.length > 11 && digitos.startsWith('55')) digitos = digitos.slice(2);
  const temFone = digitos.length >= 10;
  const celular = digitos.length === 11 && digitos[2] === '9';
  const email = texto(p.email);
  const site = texto(p.website);
  return {
    phoneLabel: temFone ? maskPhoneBR(digitos) : texto(p.phone),
    phoneHref: temFone ? `tel:+55${digitos}` : null,
    whatsappHref: celular ? `https://wa.me/55${digitos}` : null,
    email,
    emailHref: email ? `mailto:${email}` : null,
    websiteLabel: site ? site.replace(/^https?:\/\//i, '').replace(/\/$/, '') : null,
    websiteHref: site ? (/^https?:\/\//i.test(site) ? site : `https://${site}`) : null,
  };
}

const precoLabel = (valor) => (valor == null ? 'A combinar' : formatCurrency(valor));

/** "PIX, dinheiro, 2 cartões e 2 convênios" — agrupa cartões e convênios quando há mais de um. */
function rotuloDasFormas(formas) {
  const cartoes = formas.filter((f) => f.paymentType === 'debitCard' || f.paymentType === 'creditCard');
  const convenios = formas.filter((f) => f.paymentType === 'healthInsurance');
  const partes = [];
  for (const f of formas) {
    if (f.paymentType === 'debitCard' || f.paymentType === 'creditCard') {
      if (f === cartoes[0]) partes.push(cartoes.length > 1 ? `${cartoes.length} cartões` : minusculo(f.name));
    } else if (f.paymentType === 'healthInsurance') {
      if (f === convenios[0]) partes.push(convenios.length > 1 ? `${convenios.length} convênios` : f.name);
    } else {
      partes.push(f.paymentType === 'pix' ? f.name : minusculo(f.name));
    }
  }
  return lista(partes);
}

// ── o cruzamento: quem faz, qual agenda, qual turno ───────────────────────────
// A regra de "este serviço aparece para o cliente?" mora aqui e é a MESMA em Locais e em
// Profissionais (professionalsVmBuilder importa): uma regra só, nunca uma cópia dela.

/** A agenda oferece o serviço: está recebendo e tem um turno ligado que o oferece. */
export const ofereceServico = (agenda, serviceId, turnos) =>
  agenda.card.operation.isOperating
  && (turnos[agenda.raw.id] || []).some((t) => t.isActive && (t.services || []).some((x) => x.serviceId === serviceId));

/**
 * Por que um serviço não aparece para o cliente, olhando as agendas de quem o faz.
 * null = aparece; 'notOffered' = há agenda recebendo, mas nenhum turno o oferece; 'broken' = as agendas
 * não oferecem horário (defeito); 'paused' = todas pausadas pelo dono (escolha); 'noSchedule' = sem agenda.
 */
export function motivoDoServico(agendas, serviceId, turnos) {
  if (agendas.some((a) => ofereceServico(a, serviceId, turnos))) return null;
  if (agendas.some((a) => a.card.operation.isOperating)) return 'notOffered';
  if (agendas.some((a) => !a.card.operation.isByChoice)) return 'broken';
  if (agendas.length > 0) return 'paused';
  return 'noSchedule';
}

/**
 * Para cada local: agendas vivas (com o veredito de Agendas), serviços e o porquê de cada um
 * aparecer ou não para o cliente.
 */
function analisarLocal(place, ctx) {
  const membros = (place.professionals || []).filter((m) => m.professionalId);
  const catalogo = (place.services || []).filter((s) => s.serviceId);
  const agendas = ctx.agendasPorLocal.get(place.id) || [];
  const agendasDe = (profId) => agendas.filter((a) => a.raw.professionalId === profId);

  const oferece = (agenda, serviceId) => ofereceServico(agenda, serviceId, ctx.turnos);

  const servicos = catalogo.map((link) => {
    const quemFaz = membros.filter((m) => (m.serviceIds || []).includes(link.serviceId));
    const agendasDeQuemFaz = quemFaz.flatMap((m) => agendasDe(m.professionalId));
    const motivo = quemFaz.length === 0 ? 'noOne' : motivoDoServico(agendasDeQuemFaz, link.serviceId, ctx.turnos);
    return { link, servico: ctx.servicos.get(link.serviceId), quemFaz, agendasDeQuemFaz, motivo };
  });

  return { membros, catalogo, agendas, agendasDe, oferece, servicos };
}

const MOTIVO = {
  noOne: 'Ninguém atende este serviço aqui.',
  notOffered: 'Nenhum turno oferece este serviço.',
  broken: 'A agenda de quem atende não oferece horário.',
  paused: 'A agenda de quem atende está pausada.',
  noSchedule: 'Quem faz este serviço não tem agenda aqui.',
};

const fix = (type, o = {}) => ({ type, professionalId: null, serviceId: null, scheduleId: null, ...o });
const nomeDoServico = (s) => s.servico?.name || 'Serviço';
const nomeDoProfissional = (ctx, id) => ctx.profissionais.get(id)?.name || 'Profissional';

function montarProntidao(place, a, ctx, endereco) {
  const issues = [];
  const nome = place.name;
  const quebradas = a.agendas.filter((g) => !g.card.operation.isOperating && !g.card.operation.isByChoice);
  const pausadas = a.agendas.filter((g) => g.card.operation.isByChoice);
  const algumaRecebe = a.agendas.some((g) => g.card.operation.isOperating);

  if (a.membros.length === 0) {
    issues.push({
      key: 'noProfessional', label: 'Ninguém atende aqui',
      why: `Sem profissional, "${nome}" não tem de quem oferecer horário — nenhum serviço daqui aparece para agendar.`,
      status: 'error', isByChoice: false, actionLabel: 'Colocar um profissional aqui', fix: fix('manageTeam'),
    });
  }
  if (a.catalogo.length === 0) {
    issues.push({
      key: 'noService', label: 'Nenhum serviço aqui',
      why: 'O cliente escolhe primeiro o serviço. Sem nenhum aqui, não há o que marcar.',
      status: 'error', isByChoice: false, actionLabel: 'Escolher os serviços daqui', fix: fix('manageCatalog'),
    });
  }
  const semAgendaNenhuma = a.membros.length > 0 && a.catalogo.length > 0 && a.agendas.length === 0;
  if (semAgendaNenhuma) {
    const unico = a.membros.length === 1 ? nomeDoProfissional(ctx, a.membros[0].professionalId) : null;
    issues.push({
      key: 'noSchedule', label: 'Sem agenda aqui',
      why: 'É a agenda que diz em que dias e horários se atende aqui. Sem ela, não sai horário nenhum — mesmo com profissional e serviço.',
      status: 'error', isByChoice: false,
      actionLabel: unico ? `Criar a agenda de ${unico} aqui` : 'Criar uma agenda aqui',
      fix: fix('createSchedule', { professionalId: a.membros[0].professionalId }),
    });
  }
  for (const g of quebradas) {
    issues.push({
      key: 'scheduleNotOperating', label: `A agenda "${g.raw.name}" não oferece horário`,
      why: g.card.operation.detail || 'A agenda existe, mas não gera horário para o cliente.',
      status: algumaRecebe ? 'warning' : 'error', isByChoice: false,
      actionLabel: 'Abrir a agenda', fix: fix('openSchedule', { scheduleId: g.raw.id }),
    });
  }

  const ninguem = a.membros.length > 0 ? a.servicos.filter((s) => s.motivo === 'noOne') : [];
  if (ninguem.length > 0) {
    const nomes = ninguem.map(nomeDoServico);
    const unicoMembro = a.membros.length === 1 ? a.membros[0].professionalId : null;
    issues.push({
      key: 'servicesWithoutProfessional',
      label: ninguem.length === 1 ? `${nomes[0]} sem ninguém para atender` : `${ninguem.length} serviços sem ninguém para atender`,
      why: ninguem.length === 1
        ? 'O serviço está no catálogo daqui, mas nenhum profissional o faz aqui — o cliente não consegue marcar.'
        : `${lista(nomes)} estão no catálogo daqui, mas nenhum profissional os faz aqui — o cliente não consegue marcar.`,
      status: 'warning', isByChoice: false, actionLabel: 'Escolher quem faz',
      fix: unicoMembro ? fix('configureProfessionalServices', { professionalId: unicoMembro }) : fix('manageTeam'),
    });
  }

  const semHorario = a.servicos.filter((s) => s.motivo === 'notOffered' || (s.motivo === 'noSchedule' && !semAgendaNenhuma));
  if (semHorario.length > 0) {
    const primeiro = semHorario[0];
    const quem = lista(primeiro.quemFaz.map((m) => nomeDoProfissional(ctx, m.professionalId)));
    const agendaQueRecebe = primeiro.agendasDeQuemFaz.find((g) => g.card.operation.isOperating);
    issues.push({
      key: 'servicesNotBookable',
      label: semHorario.length === 1 ? `${nomeDoServico(primeiro)} sem horário` : `${semHorario.length} serviços sem horário`,
      why: primeiro.motivo === 'notOffered'
        ? `${quem} faz ${semHorario.length === 1 ? 'este serviço' : nomeDoServico(primeiro)} aqui, mas nenhum turno da agenda o oferece — o cliente não vê horário para ele.`
        : `${quem} faz ${semHorario.length === 1 ? 'este serviço' : nomeDoServico(primeiro)} aqui, mas não tem agenda aqui.`,
      status: 'warning', isByChoice: false,
      actionLabel: agendaQueRecebe ? 'Abrir a agenda' : 'Criar a agenda aqui',
      fix: agendaQueRecebe
        ? fix('openSchedule', { scheduleId: agendaQueRecebe.raw.id })
        : fix('createSchedule', { professionalId: primeiro.quemFaz[0]?.professionalId ?? null }),
    });
  }

  const pausadosPorEscolha = a.servicos.filter((s) => s.motivo === 'paused');
  if (pausadas.length > 0 && (algumaRecebe || quebradas.length > 0)) {
    const g = pausadas[0];
    const nomes = pausadosPorEscolha.map(nomeDoServico);
    issues.push({
      key: 'schedulesPaused',
      label: pausadas.length === 1 ? `A agenda "${g.raw.name}" está pausada` : `${pausadas.length} agendas pausadas`,
      why: `Foi você quem pausou. ${nomes.length
        ? `${lista(nomes)} ${nomes.length === 1 ? 'volta' : 'voltam'} a aparecer quando você reativar.`
        : 'Nenhum serviço deixou de aparecer por isso: outra agenda cobre os mesmos.'}`,
      status: 'neutral', isByChoice: true, actionLabel: 'Ver a agenda', fix: fix('openSchedule', { scheduleId: g.raw.id }),
    });
  }

  if (endereco.isMissing) {
    issues.push({
      key: 'noAddress', label: 'Sem endereço',
      why: `Se "${nome}" é uma sala ou cadeira dentro de outra unidade, tudo bem. Se tem endereço próprio, a página não mostra onde fica — e o cliente pergunta pelo WhatsApp.`,
      status: 'info', isByChoice: false, actionLabel: 'Informar o endereço', fix: fix('editAddress'),
    });
  }

  const recebem = a.servicos.filter((s) => s.motivo === null);
  const isBookable = recebem.length > 0;
  const tudoPausado = !isBookable && a.servicos.length > 0 && a.servicos.every((s) => s.motivo === 'paused');

  if (tudoPausado) {
    return {
      isBookable: false, isByChoice: true, status: 'neutral',
      headline: pausadas.length === 1 ? 'Agenda pausada por você' : 'Agendas pausadas por você',
      detail: 'Ninguém consegue marcar aqui enquanto estiver pausado. É só reativar quando quiser.',
      issues: issues.filter((i) => i.status === 'info' || i.status === 'neutral'),
    };
  }

  if (!isBookable) {
    // O que era "parte não aparece" passa a ser o motivo de nada aparecer.
    const bloqueios = issues.map((i) => (i.status === 'warning' ? { ...i, status: 'error' } : i));
    const tem = (k) => issues.some((i) => i.key === k);
    let detail;
    if (tem('noProfessional') && tem('noService')) detail = 'Ainda sem ninguém e sem nenhum serviço.';
    else if (tem('noProfessional')) detail = 'Ninguém atende aqui ainda.';
    else if (tem('noService')) detail = 'Nenhum serviço aqui ainda.';
    else if (semAgendaNenhuma) {
      detail = a.membros.length === 1
        ? `${nomeDoProfissional(ctx, a.membros[0].professionalId)} atende aqui, mas não tem agenda aqui.`
        : 'Quem atende aqui não tem agenda aqui.';
    } else if (quebradas.length > 0 && !algumaRecebe) {
      detail = quebradas.length === 1 ? 'A agenda está ligada, mas não oferece horário.' : 'As agendas daqui não oferecem horário.';
    } else if (ninguem.length === a.servicos.length) detail = 'Nenhum serviço daqui tem quem atenda.';
    else detail = 'Nenhum serviço daqui aparece para o cliente marcar.';
    return { isBookable: false, isByChoice: false, status: 'error', headline: 'Não recebe agendamento', detail, issues: bloqueios };
  }

  const defeitos = issues.filter((i) => i.status === 'warning' || i.status === 'error');
  const naoAparecem = a.servicos.filter((s) => s.motivo !== null);
  let detail = null;
  if (defeitos.length > 0) {
    if (naoAparecem.some((s) => s.motivo !== 'paused')) {
      detail = naoAparecem.length === 1
        ? `Mas ${nomeDoServico(naoAparecem[0])} não aparece para o cliente marcar.`
        : `Mas ${naoAparecem.length} dos ${a.servicos.length} serviços não aparecem para o cliente marcar agora.`;
    } else {
      detail = 'Mas uma das agendas daqui não oferece horário.';
    }
  }
  return {
    isBookable: true, isByChoice: false, status: defeitos.length > 0 ? 'warning' : 'success',
    headline: 'Recebe agendamento', detail, issues,
  };
}

// ── movimento ────────────────────────────────────────────────────────────────

function movimentoDoLocal(placeId, a, ctx) {
  const futuros = a.agendas.reduce((soma, g) => soma + (g.raw.futureAppointmentsCount || 0), 0);
  const proximo = a.agendas
    .map((g) => g.raw.nextAppointmentAt)
    .filter(Boolean)
    .sort((x, y) => Date.parse(x) - Date.parse(y))[0] || null;
  const linhas = ctx.atividadePorLocal.get(placeId) || [];
  const ultimos30 = linhas.reduce((soma, l) => soma + (l.last30Days || 0), 0);
  const online30 = linhas.reduce((soma, l) => soma + (l.onlineLast30Days || 0), 0);
  const ultimo = linhas.map((l) => l.lastAppointmentAt).filter(Boolean).sort((x, y) => Date.parse(y) - Date.parse(x))[0] || null;
  const aguardando = linhas.reduce((soma, l) => soma + (l.awaitingConfirmation || 0), 0);
  return { futuros, proximo, ultimos30, online30, ultimo, aguardando, linhas };
}

function montarAtividade(m, now) {
  const nunca = m.futuros === 0 && !m.ultimo;
  return {
    futureCount: m.futuros,
    futureLabel: m.futuros > 0 ? plural(m.futuros, 'marcado', 'marcados') : null,
    nextAppointmentLabel: m.proximo ? `Próximo: ${quando(m.proximo, now)}` : null,
    last30DaysLabel: m.ultimos30 > 0 ? `${plural(m.ultimos30, 'atendimento', 'atendimentos')} nos últimos 30 dias` : null,
    onlineShareLabel: m.ultimos30 > 0 && m.online30 > 0
      ? `${Math.round((m.online30 / m.ultimos30) * 100)}% ${m.online30 === 1 && m.ultimos30 === 1 ? 'chegou' : 'chegaram'} pela página ou pelo link`
      : null,
    lastAppointmentLabel: m.futuros === 0 && m.ultimo ? `Último atendimento: ${diaCurto(m.ultimo, now)}` : null,
    emptyLabel: nunca ? 'Ainda sem nenhum agendamento aqui' : null,
    awaitingConfirmationCount: m.aguardando,
    awaitingConfirmationLabel: m.aguardando > 0 ? `${m.aguardando} aguardando confirmação` : null,
  };
}

// ── ausências ────────────────────────────────────────────────────────────────

function ausenciasDoLocal(placeId, ctx, now) {
  return (ctx.bloqueiosPorLocal.get(placeId) || [])
    .filter((b) => valida(b.endDate) && Date.parse(b.endDate) >= now.getTime())
    .sort((x, y) => Date.parse(x.startDate) - Date.parse(y.startDate));
}

function notaDeAusencia(b, now) {
  if (!b) return null;
  const agora = Date.parse(b.startDate) <= now.getTime();
  return {
    isNow: agora,
    label: agora ? `Ausente até ${diaCurto(b.endDate, now)}` : `Ausente de ${diaCurto(b.startDate, now)} a ${diaCurto(b.endDate, now)}`,
    reason: texto(b.reason),
    status: agora ? 'warning' : 'info',
  };
}

// ── o cartão ─────────────────────────────────────────────────────────────────

function pessoa(ctx, profId) {
  const p = ctx.profissionais.get(profId);
  const name = p?.name || 'Profissional';
  return { id: profId, name, initials: getInitials(name), photoUrl: ctx.fotoDe(p?.userId, profId) };
}

function montarCard(place, ctx, now) {
  const a = analisarLocal(place, ctx);
  const address = montarEndereco(place);
  const readiness = montarProntidao(place, a, ctx, address);
  const mov = movimentoDoLocal(place.id, a, ctx);

  // Equipe na ordem de movimento: quem mais atende aqui primeiro.
  const pesoDe = (profId) => {
    const ag = a.agendasDe(profId).reduce((s, g) => s + (g.raw.futureAppointmentsCount || 0), 0);
    const hist = mov.linhas.find((l) => l.professionalId === profId)?.last30Days || 0;
    return ag + hist;
  };
  const membrosOrdenados = [...a.membros].sort((x, y) => pesoDe(y.professionalId) - pesoDe(x.professionalId)
    || nomeDoProfissional(ctx, x.professionalId).localeCompare(nomeDoProfissional(ctx, y.professionalId), 'pt-BR'));

  const formas = (place.payments || []).map((pp) => ({ name: pp.name, paymentType: TIPO_DE_PAGAMENTO[pp.type] || 'other' }));
  const semNinguem = a.membros.length > 0 ? a.servicos.filter((s) => s.motivo === 'noOne').length : a.catalogo.length;
  const paginas = ctx.paginasPorLocal.get(place.id) || [];
  const ausencias = ausenciasDoLocal(place.id, ctx, now);
  const proximaAusencia = ausencias.find((b) => Date.parse(b.startDate) - now.getTime() < 30 * DIA_MS) || null;
  const nomeAusente = proximaAusencia ? nomeDoProfissional(ctx, ctx.vinculos.get(proximaAusencia.placeProfessionalId)?.professionalId) : null;

  const card = {
    id: place.id,
    name: place.name,
    initials: getInitials(place.name),
    description: texto(place.description),
    address,
    contact: montarContato(place),
    team: {
      count: a.membros.length,
      label: a.membros.length === 0 ? 'Ninguém atende aqui' : plural(a.membros.length, 'profissional', 'profissionais'),
      members: membrosOrdenados.slice(0, 5).map((m) => pessoa(ctx, m.professionalId)),
      moreLabel: a.membros.length > 5 ? `+${a.membros.length - 5}` : null,
    },
    catalog: {
      count: a.catalogo.length,
      label: a.catalogo.length === 0 ? 'Nenhum serviço' : plural(a.catalogo.length, 'serviço', 'serviços'),
      withoutProfessionalCount: semNinguem,
      withoutProfessionalLabel: semNinguem > 0 ? `${semNinguem} sem ninguém para atender` : null,
    },
    payments: formas.length === 0
      ? { isConfigured: false, count: 0, label: null, items: [], emptyLabel: 'Formas de pagamento não informadas — o cliente vê o preço padrão de cada serviço.' }
      : { isConfigured: true, count: formas.length, label: rotuloDasFormas(formas), items: formas, emptyLabel: null },
    readiness,
    activity: montarAtividade(mov, now),
    presence: {
      pagesCount: paginas.length,
      label: paginas.length > 0 ? `Em ${plural(paginas.length, 'página', 'páginas')} no ar` : null,
      hasOwnPage: paginas.some((p) => p.scope === 'place'),
    },
    absentLabel: proximaAusencia
      ? (Date.parse(proximaAusencia.startDate) <= now.getTime()
        ? `${nomeAusente} ausente até ${diaCurto(proximaAusencia.endDate, now)}`
        : `${nomeAusente} fica fora de ${diaCurto(proximaAusencia.startDate, now)} a ${diaCurto(proximaAusencia.endDate, now)}`)
      : null,
    sinceLabel: place.createdAt ? `Desde ${mesAno(place.createdAt)}` : '',
  };
  return { card, a, mov, membrosOrdenados, paginas, ausencias, peso: mov.futuros + mov.ultimos30 };
}

// ── o local aberto ───────────────────────────────────────────────────────────

const CAMPOS = [
  { key: 'address', label: 'Endereço', tem: (c) => !c.address.isMissing },
  { key: 'phone', label: 'Telefone', tem: (c) => !!c.contact.phoneLabel },
  { key: 'email', label: 'E-mail', tem: (c) => !!c.contact.email },
  { key: 'website', label: 'Site', tem: (c) => !!c.contact.websiteLabel },
  { key: 'description', label: 'Descrição', tem: (c) => !!c.description },
];

function montarMembro(m, place, x, ctx, now) {
  const { a } = x;
  const prof = ctx.profissionais.get(m.professionalId);
  const quem = pessoa(ctx, m.professionalId);
  const aqui = a.servicos.filter((s) => s.quemFaz.includes(m));
  const agendas = a.agendasDe(m.professionalId);
  const recebendo = agendas.filter((g) => g.card.operation.isOperating);

  let readiness;
  if (aqui.length === 0) {
    readiness = { isBookable: false, isByChoice: false, status: 'error', headline: 'Não recebe agendamento', detail: 'Não faz nenhum serviço aqui.', issues: [] };
  } else if (agendas.length === 0) {
    readiness = { isBookable: false, isByChoice: false, status: 'error', headline: 'Não recebe agendamento', detail: 'Não tem agenda aqui.', issues: [] };
  } else if (recebendo.length === 0) {
    readiness = agendas.every((g) => g.card.operation.isByChoice)
      ? { isBookable: false, isByChoice: true, status: 'neutral', headline: agendas.length === 1 ? 'Agenda pausada por você' : 'Agendas pausadas por você', detail: null, issues: [] }
      : { isBookable: false, isByChoice: false, status: 'error', headline: 'Não recebe agendamento', detail: agendas.find((g) => !g.card.operation.isByChoice)?.card.operation.detail || null, issues: [] };
  } else {
    const fora = aqui.filter((s) => !recebendo.some((g) => a.oferece(g, s.link.serviceId)));
    readiness = fora.length === 0
      ? { isBookable: true, isByChoice: false, status: 'success', headline: 'Recebe agendamento', detail: null, issues: [] }
      : {
        isBookable: true, isByChoice: false, status: 'warning', headline: 'Recebe agendamento',
        detail: fora.length === 1
          ? `Mas ${nomeDoServico(fora[0])} não aparece para o cliente marcar.`
          : `Mas ${fora.length} dos ${aqui.length} serviços que faz aqui não aparecem para o cliente marcar.`,
        issues: [],
      };
  }

  const futuros = agendas.reduce((s, g) => s + (g.raw.futureAppointmentsCount || 0), 0);
  const proximo = agendas.map((g) => g.raw.nextAppointmentAt).filter(Boolean).sort((p, q) => Date.parse(p) - Date.parse(q))[0];
  const n = a.catalogo.length;
  const ausencia = x.ausencias.find((b) => ctx.vinculos.get(b.placeProfessionalId)?.professionalId === m.professionalId);

  return {
    professionalId: m.professionalId,
    name: quem.name,
    initials: quem.initials,
    photoUrl: quem.photoUrl,
    specialty: texto(prof?.specialty),
    isReceived: !!prof?.isShared,
    ownerName: prof?.isShared ? texto(prof.ownerName) : null,
    serviceNames: aqui.map(nomeDoServico),
    servicesHereLabel: aqui.length === 0 ? 'Sem serviço aqui'
      : n === 1 ? 'Faz o único serviço daqui'
        : aqui.length === n ? `Faz os ${n} serviços daqui` : `Faz ${aqui.length} dos ${n} serviços daqui`,
    readiness,
    absence: notaDeAusencia(ausencia, now),
    activityLabel: futuros > 0
      ? `${plural(futuros, 'marcado', 'marcados')} aqui${proximo ? ` · próximo ${quando(proximo, now)}` : ''}`
      : null,
    canConfigure: !prof?.isShared,
  };
}

function montarCatalogo(place, x, ctx) {
  const precosProprios = ctx.precosProprios;
  const ordemDaForma = new Map((place.payments || []).map((pp, i) => [pp.paymentId, i]));
  const formaPorId = new Map((place.payments || []).map((pp) => [pp.paymentId, pp]));
  const localTravado = !x.card.readiness.isBookable && !x.card.readiness.isByChoice;
  const grupos = new Map();
  for (const s of x.a.servicos) {
    const servico = s.servico || {};
    const prices = (s.link.payments || [])
      .filter((sp) => formaPorId.has(sp.paymentId))
      .sort((p, q) => (ordemDaForma.get(p.paymentId) ?? 999) - (ordemDaForma.get(q.paymentId) ?? 999))
      .map((sp) => {
        const forma = formaPorId.get(sp.paymentId);
        const proprios = (precosProprios.get(sp.id) || [])
          .filter((pp) => s.quemFaz.some((m) => m.professionalId === pp.professionalId));
        return {
          paymentName: forma.name,
          paymentType: TIPO_DE_PAGAMENTO[forma.type] || 'other',
          priceLabel: precoLabel(sp.defaultPrice),
          isDefault: (sp.defaultPrice ?? null) === (servico.defaultPrice ?? null),
          isToBeAgreed: sp.defaultPrice == null,
          customLabel: proprios.length
            ? proprios.map((pp) => `${nomeDoProfissional(ctx, pp.professionalId)} cobra ${formatCurrency(pp.customPrice)}`).join('; ')
            : null,
        };
      });
    const categoria = texto(servico.category) || 'Sem categoria';
    if (!grupos.has(categoria)) grupos.set(categoria, []);
    grupos.get(categoria).push({
      serviceId: s.link.serviceId,
      name: nomeDoServico(s),
      durationLabel: servico.defaultDurationMinutes ? formatDuration(servico.defaultDurationMinutes) : null,
      basePriceLabel: precoLabel(servico.defaultPrice),
      basePriceIsToBeAgreed: servico.defaultPrice == null,
      prices,
      professionals: s.quemFaz.map((m) => pessoa(ctx, m.professionalId)),
      hasNoProfessional: s.quemFaz.length === 0,
      isBookable: s.motivo === null,
      notBookableReason: s.motivo ? MOTIVO[s.motivo] : null,
      // Pausa é escolha (neutro). Defeito pesa como pesa no local: se nada aqui recebe, é o que impede.
      notBookableStatus: !s.motivo ? null
        : s.motivo === 'paused' ? 'neutral'
          : localTravado ? 'error' : 'warning',
      notBookableIsByChoice: s.motivo === 'paused',
    });
  }
  return [...grupos.entries()]
    .sort(([p], [q]) => (p === 'Sem categoria') - (q === 'Sem categoria') || p.localeCompare(q, 'pt-BR'))
    .map(([category, items]) => ({ category, items: items.sort(porNome) }));
}

function montarFormasAceitas(place, x, ctx) {
  return (place.payments || []).map((pp) => {
    const combos = x.a.catalogo.flatMap((link) => (link.payments || []).filter((sp) => sp.paymentId === pp.paymentId));
    const servicos = combos.length;
    const proprios = combos.reduce((soma, sp) => soma + (ctx.precosProprios.get(sp.id) || []).length, 0);
    return {
      id: pp.id,
      name: pp.name,
      paymentType: TIPO_DE_PAGAMENTO[pp.type] || 'other',
      servicesCount: servicos,
      usageLabel: servicos ? `Em ${plural(servicos, 'serviço', 'serviços')}` : 'Nenhum serviço com preço nesta forma',
      customPricesCount: proprios,
      removalImpactLabel: servicos || proprios
        ? `Tirar apaga o preço em ${plural(servicos, 'serviço', 'serviços')}${proprios ? ` e ${plural(proprios, 'preço próprio', 'preços próprios')}` : ''}`
        : null,
    };
  });
}

function montarAgendaDoLocal(g, ctx, now) {
  const turnos = (ctx.turnos[g.raw.id] || []).filter((t) => t.isActive);
  const dias = DIAS.map((d) => turnos.some((t) => !!t[d]));
  const unico = g.card.shifts.filter((t) => t.isActive);
  const futuros = g.raw.futureAppointmentsCount || 0;
  return {
    id: g.raw.id,
    name: g.raw.name,
    professionalName: ctx.profissionais.get(g.raw.professionalId)?.name || g.raw.professionalName || 'Profissional',
    headline: g.card.operation.headline,
    status: g.card.operation.status,
    isByChoice: g.card.operation.isByChoice,
    weekLabel: unico.length === 0 ? null
      : unico.length === 1 ? `${unico[0].daysLabel} · ${unico[0].timeLabel}`
        : `${daysLabel(dias)} · ${unico.length} turnos`,
    daysActive: dias,
    capacityLabel: (g.raw.maxAttendees || 1) > 1 ? `Turma · até ${g.raw.maxAttendees} por horário` : null,
    activityLabel: futuros > 0
      ? `${plural(futuros, 'marcado', 'marcados')}${g.raw.nextAppointmentAt ? ` · próximo ${quando(g.raw.nextAppointmentAt, now)}` : ''}`
      : null,
  };
}

function montarDetalhe(place, x, ctx, now) {
  const { card, mov } = x;
  const faltam = CAMPOS.filter((c) => !c.tem(card));
  const stats = [];
  if (mov.ultimos30 > 0 || mov.futuros > 0 || mov.ultimo) {
    stats.push({ key: 'last30Days', label: 'Atendimentos nos últimos 30 dias', value: String(mov.ultimos30), hint: mov.ultimos30 === 0 && mov.ultimo ? `O último foi em ${diaCurto(mov.ultimo, now)}.` : null });
    if (mov.ultimos30 > 0) {
      stats.push({ key: 'online', label: 'Chegaram pela página ou pelo link', value: `${Math.round((mov.online30 / mov.ultimos30) * 100)}%`, hint: 'O resto foi lançado no painel.' });
    }
    stats.push({ key: 'future', label: 'Agendamentos marcados', value: String(mov.futuros), hint: mov.proximo ? `Próximo: ${quando(mov.proximo, now)}` : null });
    if (mov.aguardando > 0) {
      stats.push({ key: 'awaitingConfirmation', label: 'Aguardando confirmação', value: String(mov.aguardando), hint: 'Confirme em Agendamentos.' });
    }
    const top = [...mov.linhas].sort((p, q) => q.last30Days - p.last30Days)[0];
    if (x.a.membros.length > 1 && top?.last30Days > 0) {
      stats.push({ key: 'busiestProfessional', label: 'Quem mais atende aqui', value: `${nomeDoProfissional(ctx, top.professionalId)} — ${top.last30Days} em 30 dias`, hint: null });
    }
  }

  return {
    id: place.id,
    card,
    completenessLabel: faltam.length ? `Cadastro com ${CAMPOS.length + 1 - faltam.length} de ${CAMPOS.length + 1} informações` : null,
    missingFields: faltam.map(({ key, label }) => ({ key, label })),
    team: x.membrosOrdenados.map((m) => montarMembro(m, place, x, ctx, now)),
    teamEmptyLabel: x.a.membros.length === 0 ? 'Ninguém atende aqui ainda. Coloque um profissional para receber agendamento aqui.' : null,
    catalog: montarCatalogo(place, x, ctx),
    payments: montarFormasAceitas(place, x, ctx),
    schedules: [...x.a.agendas]
      .sort((p, q) => Number(q.card.operation.isOperating) - Number(p.card.operation.isOperating) || porNome(p.raw, q.raw))
      .map((g) => montarAgendaDoLocal(g, ctx, now)),
    activityStats: stats,
    activityEmptyLabel: stats.length ? null : 'Assim que o primeiro cliente marcar aqui, o movimento aparece.',
    pages: x.paginas.map((p) => ({
      id: p.pageId,
      title: p.title,
      typeLabel: TIPO_DE_PAGINA[p.pageType] || 'Página',
      scopeLabel: ESCOPO_DA_PAGINA[p.scope] || 'De toda a conta',
      url: `${PAGES_URL}/${p.slug}`,
    })),
    absences: x.ausencias.map((b) => {
      const agora = Date.parse(b.startDate) <= now.getTime();
      const dias = diasEntre(b.startDate, b.endDate);
      return {
        id: b.id,
        professionalName: nomeDoProfissional(ctx, ctx.vinculos.get(b.placeProfessionalId)?.professionalId),
        label: agora
          ? `Até ${diaCurto(b.endDate, now)}`
          : `${diaCurto(b.startDate, now)} a ${diaCurto(b.endDate, now)} · ${plural(dias, 'dia', 'dias')}`,
        reason: texto(b.reason),
        isNow: agora,
      };
    }),
  };
}

// ── resumo, filtros, vazio ───────────────────────────────────────────────────

function montarResumo(montados, labels, now) {
  const total = montados.length;
  const nomeDe = (n) => (n === 1 ? minusculo(labels.singular) : minusculo(labels.title));
  const recebem = montados.filter((x) => x.card.readiness.isBookable).length;
  const atencao = montados.filter((x) => !x.card.readiness.isBookable && !x.card.readiness.isByChoice).length;
  const semEndereco = montados.filter((x) => x.card.address.isMissing).length;
  const profissionais = new Set(montados.flatMap((x) => x.a.membros.map((m) => m.professionalId))).size;
  const servicos = new Set(montados.flatMap((x) => x.a.catalogo.map((s) => s.serviceId))).size;
  const futuros = montados.reduce((s, x) => s + x.mov.futuros, 0);
  const proximo = montados
    .filter((x) => x.mov.proximo)
    .sort((p, q) => Date.parse(p.mov.proximo) - Date.parse(q.mov.proximo))[0];

  return {
    total,
    totalLabel: total === 0 ? `Nenhum cadastro em ${minusculo(labels.title)}` : `${total} ${nomeDe(total)}`,
    bookableCount: recebem,
    bookableLabel: total === 1
      ? (recebem ? 'Recebe agendamento' : 'Não recebe agendamento')
      : total === 0 ? 'Nada recebe agendamento ainda' : `${recebem} ${recebem === 1 ? 'recebe' : 'recebem'} agendamento`,
    attentionCount: atencao,
    attentionLabel: atencao > 0 && total > 1 ? `${atencao} não ${atencao === 1 ? 'recebe' : 'recebem'} agendamento` : null,
    noAddressLabel: semEndereco === 0 ? null
      : total === 1 ? 'Sem endereço — a página não mostra onde fica'
        : `${semEndereco} sem endereço — a página não mostra onde ${semEndereco === 1 ? 'fica' : 'ficam'}`,
    teamLabel: profissionais === 0 ? null
      : total > 1 ? `${plural(profissionais, 'profissional', 'profissionais')} em ${total} ${nomeDe(total)}`
        : plural(profissionais, 'profissional', 'profissionais'),
    catalogLabel: servicos > 0 ? plural(servicos, 'serviço oferecido', 'serviços oferecidos') : null,
    futureAppointmentsLabel: futuros > 0 ? plural(futuros, 'agendamento marcado', 'agendamentos marcados') : null,
    nextAppointmentLabel: proximo
      ? `Próximo: ${quando(proximo.mov.proximo, now)}${total > 1 ? ` · ${proximo.card.name}` : ''}`
      : null,
    status: total === 0 ? 'neutral'
      : atencao > 0 ? 'error'
        : montados.some((x) => x.card.readiness.status === 'warning') ? 'warning' : 'success',
  };
}

const normalizar = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function casaBusca(place, busca) {
  if (!busca) return true;
  const alvo = normalizar(busca);
  const digitos = busca.replace(/\D/g, '');
  return [place.name, place.addressNeighborhood, place.addressCity, place.addressStreet, place.address]
    .some((v) => normalizar(v).includes(alvo))
    || (digitos.length >= 3 && String(place.phone || '').replace(/\D/g, '').includes(digitos));
}

const ORDENS = {
  name_asc: (p, q) => porNome(p.card, q.card),
  name_desc: (p, q) => porNome(q.card, p.card),
  city_asc: (p, q) => String(p.place.addressCity || '￿').localeCompare(String(q.place.addressCity || '￿'), 'pt-BR') || porNome(p.card, q.card),
  busiest: (p, q) => q.peso - p.peso || porNome(p.card, q.card),
};

// ── entrada ──────────────────────────────────────────────────────────────────

/**
 * @param {Object} args
 * @param {Object} args.data         usePlacesCRUD().data — { places, professionals, services, payments,
 *                                   activity, pages, professionalPrices } (GET /api/places)
 * @param {Array}  args.schedules    GET /api/schedules (ScheduleForListDto[] com métricas)
 * @param {Object|null} args.entities GET /api/entities — turnos, serviços de cada turno, ausências e
 *                                   vínculos; null = ainda não chegou (a tela segura em 'loading')
 * @param {Function} args.getLabel   tema » labels (useThemeCustom().getLabel)
 * @param {Object} args.filters      { searchText, sortOrder, segment }
 * @param {string|null} args.selectedId
 * @param {boolean} args.loading
 * @param {*} args.error
 * @param {(donoId:string, profissionalId:string)=>string} args.fotoDe
 * @param {Date} [args.now]
 */
export function buildPlacesVm({
  data = {},
  schedules = [],
  entities = null,
  getLabel,
  filters = {},
  selectedId = null,
  loading = false,
  error = null,
  fotoDe = () => '',
  now = new Date(),
}) {
  const { labels, examples } = buildPlacesLabels(getLabel);
  const { searchText = '', sortOrder = 'name_asc', segment = 'all' } = filters;
  const places = data.places || [];

  const filtrosBase = (segmentOptions, isFiltered, resultLabel) => ({
    searchText, sortOrder, sortOptions: SORT_OPTIONS, segment, segmentOptions,
    isFiltered, canClear: isFiltered, resultLabel,
  });
  const vazio = (viewState, errorMessage) => ({
    viewState, errorMessage, labels, summary: montarResumo([], labels, now), planUsage: null,
    filters: filtrosBase([], false, null), items: [], emptyState: null, selectedId: null, detail: null,
  });

  // Erro tem precedência: falhou, mostra a falha (nunca um skeleton eterno).
  if (error) return vazio('error', 'Não foi possível carregar agora. Verifique sua conexão e tente de novo.');

  // Turnos e ausências vêm de /entities, separados da lista. Sem eles todo local pareceria não
  // receber agendamento — acusar sem ter os dados é pior do que esperar (mesma guarda de Agendas).
  if (loading || (places.length > 0 && !entities)) return vazio('loading', null);

  const ctx = montarContexto(data, schedules, entities, fotoDe);
  const montados = places.map((place) => ({ place, ...montarCard(place, ctx, now) }));
  const summary = montarResumo(montados, labels, now);

  if (montados.length === 0) {
    return {
      ...vazio('empty', null),
      summary,
      emptyState: {
        situation: 'firstTime',
        title: 'Comece cadastrando onde você atende',
        body: 'É onde ficam a equipe, os serviços e a agenda — pode ser uma unidade com endereço ou uma sala, uma cadeira.',
        actionLabel: labels.actionLabel,
        examples,
      },
    };
  }

  const atencao = (x) => !x.card.readiness.isBookable && !x.card.readiness.isByChoice;
  const SEGMENTOS = {
    all: () => true,
    attention: atencao,
    noAddress: (x) => x.card.address.isMissing,
  };
  const segmentoValido = SEGMENTOS[segment] ? segment : 'all';
  const segmentOptions = [
    { value: 'all', label: 'Todos', count: montados.length },
    { value: 'attention', label: 'Não recebem agendamento', count: montados.filter(atencao).length },
    { value: 'noAddress', label: 'Sem endereço', count: montados.filter(SEGMENTOS.noAddress).length },
  ].filter((o) => o.value === 'all' || o.count > 0);

  const visiveis = montados
    .filter((x) => SEGMENTOS[segmentoValido](x) && casaBusca(x.place, searchText.trim()))
    .sort(ORDENS[sortOrder] || ORDENS.name_asc);
  const isFiltered = !!searchText.trim() || segmentoValido !== 'all';

  // Um local só e sem filtro: abre direto (pedido do CD — é o caso de 918 de 969 contas).
  const unico = montados.length === 1 && !isFiltered ? montados[0].card.id : null;
  const aberto = montados.find((x) => x.card.id === (selectedId || unico)) || null;

  return {
    viewState: 'ready',
    errorMessage: null,
    labels,
    summary,
    planUsage: null, // Plans:EnforcementEnabled=false em PRD — ver o contrato.
    filters: filtrosBase(segmentOptions, isFiltered, isFiltered ? `${visiveis.length} de ${montados.length}` : null),
    items: visiveis.map((x) => x.card),
    emptyState: visiveis.length === 0
      ? {
        situation: 'filtered',
        title: searchText.trim() ? `Nada encontrado para "${searchText.trim()}"` : 'Nada neste filtro',
        body: 'A busca olha o nome, o bairro, a cidade e o telefone.',
        actionLabel: searchText.trim() ? 'Limpar busca' : 'Ver todos',
        examples: [],
      }
      : null,
    selectedId: aberto ? aberto.card.id : null,
    detail: aberto ? montarDetalhe(aberto.place, aberto, ctx, now) : null,
  };
}

/**
 * Turnos por agenda no formato do ScheduleShiftDto (o que buildScheduleCard lê), a partir do espelho
 * (GET /api/entities). Também usado por Profissionais.
 */
export function montarTurnos(entities, nomeDoServicoPorId) {
  const servicosDoTurno = new Map();
  for (const sss of entities?.scheduleShiftServices || []) {
    if (!servicosDoTurno.has(sss.scheduleShiftId)) servicosDoTurno.set(sss.scheduleShiftId, []);
    servicosDoTurno.get(sss.scheduleShiftId).push({ serviceId: sss.serviceId, serviceName: nomeDoServicoPorId(sss.serviceId) });
  }
  const turnos = {};
  for (const t of entities?.scheduleShifts || []) {
    (turnos[t.scheduleId] ||= []).push({ ...t, services: servicosDoTurno.get(t.id) || [] });
  }
  return turnos;
}

/** Índices montados uma vez por render: tudo vira Map por id. */
function montarContexto(data, schedules, entities, fotoDe) {
  const profissionais = new Map((data.professionals || []).map((p) => [p.id, p]));
  const servicos = new Map([...(entities?.services || []), ...(data.services || [])].map((s) => [s.id, s]));
  const turnos = montarTurnos(entities, (id) => servicos.get(id)?.name || null);

  const agendasPorLocal = new Map();
  for (const s of schedules || []) {
    if (String(s.status || 'Active').toLowerCase() === 'deleted') continue;
    if (!agendasPorLocal.has(s.placeId)) agendasPorLocal.set(s.placeId, []);
    agendasPorLocal.get(s.placeId).push({ raw: s, card: buildScheduleCard(s, turnos[s.id] || []) });
  }

  const vinculos = new Map((entities?.placeProfessionals || []).map((pp) => [pp.id, pp]));
  const bloqueiosPorLocal = new Map();
  for (const b of entities?.scheduleBlocks || []) {
    const placeId = vinculos.get(b.placeProfessionalId)?.placeId;
    if (!placeId) continue;
    if (!bloqueiosPorLocal.has(placeId)) bloqueiosPorLocal.set(placeId, []);
    bloqueiosPorLocal.get(placeId).push(b);
  }

  const agrupar = (lista, chave) => {
    const m = new Map();
    for (const item of lista || []) {
      if (!m.has(item[chave])) m.set(item[chave], []);
      m.get(item[chave]).push(item);
    }
    return m;
  };

  return {
    profissionais,
    servicos,
    turnos,
    agendasPorLocal,
    vinculos,
    bloqueiosPorLocal,
    atividadePorLocal: agrupar(data.activity, 'placeId'),
    paginasPorLocal: agrupar(data.pages, 'placeId'),
    precosProprios: agrupar(data.professionalPrices, 'servicePaymentId'),
    fotoDe,
  };
}

// Peças que Profissionais reaproveita (mesmas datas, endereço, contato, preço e agenda — o mesmo produto).
export {
  diaBRT, quando, diaCurto, mesAno, diasEntre, plural, texto, lista, porNome, precoLabel,
  montarEndereco, montarContato, montarAgendaDoLocal, TIPO_DE_PAGINA, TIPO_DE_PAGAMENTO, PAGES_URL,
};
